import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, Download, Heart, Mail, Printer, School } from "lucide-react";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import Footer from "@/sections/Footer";
import { Eyebrow, MaskedLine, Reveal } from "@/components/Reveal";

const SHEET_PARTS = [
  {
    n: "1",
    title: "Twelve affirmations",
    text: "Each with a character from the book. I am Brave, I am Confident, I am Kind, I am Helpful, I am Happy, I am Healthy, I am Strong, I am a Leader, I am Blessed, I am Thankful, I am Smart and I am Creative.",
  },
  {
    n: "2",
    title: "I am ________.",
    text: "Your child picks their favourite affirmation and writes it on the line. Grown-ups can help, or older children can copy the word themselves.",
  },
  {
    n: "3",
    title: "Draw yourself",
    text: "A big space for your child to draw themselves being brave, kind, strong, or whatever they chose.",
  },
];

const HOME_TIPS = [
  {
    title: "Read them aloud together",
    text: "Point to each character and say the sentence together. Ask your child which one feels most like them today.",
  },
  {
    title: "Pick one a day",
    text: "Choose an affirmation in the morning and look out for moments when your child shows it. Then say it back to them: “That was so brave!”",
  },
  {
    title: "Use the mirror",
    text: "Children love saying “I am strong!” to their own reflection, especially with a superhero pose like Bailey's.",
  },
  {
    title: "Make it part of bedtime",
    text: "End the day with one or two affirmations. It's a calm, warm way to finish.",
  },
  {
    title: "Put the sheet where you'll see it",
    text: "Stick the finished sheet on the fridge or bedroom wall as a daily reminder.",
  },
];

export default function BlogAffirmationsPost() {
  const navigate = useNavigate();
  const goToWorksheets = () => navigate("/", { state: { scrollTo: "worksheets" } });

  return (
    <div className="grain">
      <Navbar />
      <main className="pt-28 sm:pt-32 pb-24 overflow-x-clip">
        <article className="max-w-3xl mx-auto px-4 sm:px-6">

          <div className="text-center mb-14">
            <Eyebrow className="mb-5">Learn with Bailey · Blog</Eyebrow>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.1] text-4xl sm:text-5xl text-walnut">
              <MaskedLine i={0}>Affirmations</MaskedLine>
              <MaskedLine i={1} className="text-terracotta">for Children</MaskedLine>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 inline-flex items-center gap-3 rounded-full bg-white/85 border border-sand pl-2 pr-5 py-2 shadow-sm"
            >
              <img src="/images/damali-author.webp" alt="Damali Allison" className="w-10 h-10 rounded-full object-cover" />
              <span className="text-sm font-semibold text-walnut">Damali Allison</span>
              <span className="text-sand">·</span>
              <span className="text-sm text-walnutmuted">Oct 7, 2026</span>
            </motion.div>
          </div>

          <Reveal>
            <p className="text-lg sm:text-xl text-walnut leading-relaxed font-display font-semibold border-l-4 border-terracotta pl-5 mb-6">
              The words children hear about themselves soon become the words they use
              about themselves. “I can't do it” and “I'm not good at this” can creep in
              surprisingly early.
            </p>
            <p className="text-base sm:text-lg text-walnutsoft leading-relaxed mb-6">
              Affirmations are a simple, fun way to fill that space with kinder words. To
              help you get started, I've made a free printable “I am” sheet with the
              characters from my book, Bailey's Affirmations.
            </p>
          </Reveal>

          <Reveal className="my-12">
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-walnut mb-5">
              What is an affirmation?
            </h2>
            <p className="text-base sm:text-lg text-walnutsoft leading-relaxed mb-5">
              An affirmation is a short, positive sentence that you say about yourself, like
              “I am brave” or “I am kind.” Said often enough, it becomes part of how a
              child sees themselves.
            </p>
            <p className="text-base sm:text-lg text-walnutsoft leading-relaxed">
              “I am” sentences work especially well for little ones. They're short enough
              to remember and easy to say out loud, and children can attach them to real
              moments. When your child helps tidy up, you can say, “You were helpful! Can
              you say it? <em className="text-walnut font-semibold not-italic">I am helpful.</em>”
            </p>
          </Reveal>

          <Reveal className="my-12">
            <div className="bg-[#FFF9E6] border border-sand rounded-[2rem] p-7 sm:p-10">
              <Eyebrow className="mb-4">Inside the printable</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-walnut mb-8">
                What's on the sheet
              </h2>
              <div className="flex flex-col gap-6">
                {SHEET_PARTS.map((p) => (
                  <div key={p.n} className="flex gap-4 items-start">
                    <span className="shrink-0 w-11 h-11 rounded-full bg-amberbrand text-cream font-display font-extrabold text-lg flex items-center justify-center shadow">
                      {p.n}
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-lg sm:text-xl text-walnut">{p.title}</h3>
                      <p className="text-sm sm:text-base text-walnutsoft leading-relaxed mt-1">{p.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal className="my-12">
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-walnut mb-4">
              How to use affirmations at home
            </h2>
            <p className="text-base sm:text-lg text-walnutsoft leading-relaxed mb-8">
              Affirmations work best little and often. These are some easy ways to fit
              them into your day:
            </p>
            <div className="flex flex-col gap-5">
              {HOME_TIPS.map((t, i) => (
                <div key={t.title} className="flex gap-4 items-start bg-white/70 border border-sand rounded-2xl p-5">
                  <Heart size={20} className="shrink-0 mt-1 text-terracotta" fill="currentColor" />
                  <div>
                    <h3 className="font-display font-bold text-base sm:text-lg text-walnut">{i + 1}. {t.title}</h3>
                    <p className="text-sm sm:text-base text-walnutsoft leading-relaxed mt-1">{t.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-base sm:text-lg text-walnut leading-relaxed font-display font-semibold text-center">
              There are no wrong answers. If your child chooses “I am happy” every single
              day, that's perfectly fine.
            </p>
          </Reveal>

          <Reveal className="my-12">
            <div className="bg-sagecream border border-sand rounded-[2rem] p-7 sm:p-10">
              <p className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-sage mb-4">
                <School size={15} /> For nurseries and teachers
              </p>
              <p className="text-base sm:text-lg text-walnutsoft leading-relaxed mb-4">
                The sheet fits easily into circle time or PSED activities. You're welcome
                to print copies for your whole class.
              </p>
              <p className="text-base sm:text-lg text-walnutsoft leading-relaxed">
                Try an “affirmation of the week”, with children spotting each other showing
                it. Or display the finished drawings together on an “I am…” wall, so every
                child can see their own words up on the board.
              </p>
            </div>
          </Reveal>

          <Reveal className="my-12">
            <div data-testid="blog-download-card" className="relative bg-walnut text-cream rounded-[2rem] p-8 sm:p-10 overflow-hidden">
              <div className="absolute w-56 h-56 rounded-full bg-terracotta/25 blur-[100px] -top-16 right-0 pointer-events-none" />
              <div className="grid sm:grid-cols-[auto_1fr] gap-8 items-center relative z-10">
                <motion.img
                  src="/images/affirm-poster-preview.webp"
                  alt="Bailey's Affirmations free printable I am sheet"
                  className="w-40 sm:w-48 mx-auto rounded-xl shadow-[0_20px_36px_-14px_rgba(0,0,0,0.5)] rotate-[-2deg]"
                  whileHover={{ rotate: 0, scale: 1.04 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14 }}
                />
                <div className="text-center sm:text-left">
                  <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl mb-3">
                    Download your free sheet
                  </h2>
                  <p className="text-sm sm:text-base text-cream/80 leading-relaxed mb-6 inline-flex items-start gap-2">
                    <Printer size={16} className="shrink-0 mt-0.5" />
                    It prints on a single sheet of A4, in colour or black and white.
                  </p>
                  <a
                    href="/downloads/affirmations-i-am-poster.pdf"
                    download="Baileys-Affirmations-I-Am-Poster.pdf"
                    data-testid="blog-download-sheet"
                    className="inline-flex items-center gap-2 rounded-full bg-amberbrand text-cream font-display font-bold text-sm sm:text-base px-8 py-4 hover:brightness-110 transition shadow-lg"
                  >
                    <Download size={18} /> Download the free “I am” sheet
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal className="my-12">
            <div className="grid sm:grid-cols-[auto_1fr] gap-8 items-center bg-white/70 border border-sand rounded-[2rem] p-7 sm:p-10">
              <img
                src="/images/book-bailey-affirmations.jpg"
                alt="Bailey's Affirmations book cover"
                className="w-36 sm:w-44 mx-auto rounded-xl shadow-[0_18px_32px_-14px_rgba(49,67,92,0.4)]"
              />
              <div>
                <Eyebrow className="mb-3">Meet everyone in Bailey's Affirmations</Eyebrow>
                <p className="text-sm sm:text-base text-walnutsoft leading-relaxed mb-4">
                  Every character on the sheet comes from my book, Bailey's Affirmations.
                  Each page brings an affirmation to life, so your child gets to know the
                  characters while learning to say kind, confident words about themselves.
                </p>
                <p className="text-sm sm:text-base text-walnutsoft leading-relaxed mb-6">
                  It's a lovely book to read at bedtime or first thing in the morning, and
                  it pairs perfectly with the free sheet.
                </p>
                <Link
                  to="/books/baileys-affirmations"
                  data-testid="blog-get-book"
                  className="group inline-flex items-center gap-2 rounded-full bg-terracotta text-cream font-display font-bold text-sm sm:text-base px-7 py-3.5 hover:bg-terracottadark transition-colors"
                >
                  <BookOpen size={17} /> Get your copy of Bailey's Affirmations
                  <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </Reveal>

          <Reveal className="my-12">
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-walnut mb-5">
              More from Bailey
            </h2>
            <p className="text-base sm:text-lg text-walnutsoft leading-relaxed mb-5">
              You'll also find a free{" "}
              <a
                href="/downloads/starts-school-gift.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-terracotta underline decoration-terracotta/40 hover:decoration-terracotta"
              >
                Bailey Starts School
              </a>{" "}
              sheet and a{" "}
              <a
                href="/downloads/london-explorer-gift.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-terracotta underline decoration-terracotta/40 hover:decoration-terracotta"
              >
                Bailey in London explorer
              </a>{" "}
              sheet on the{" "}
              <button
                type="button"
                data-testid="blog-worksheets-link"
                onClick={goToWorksheets}
                className="font-semibold text-terracotta underline decoration-terracotta/40 hover:decoration-terracotta cursor-pointer"
              >
                worksheets &amp; printables page
              </button>
              .
            </p>
            <p className="text-base sm:text-lg text-walnutsoft leading-relaxed">
              I also visit nurseries and primary schools across London for interactive
              Bailey story-time sessions. I'm DBS-checked, and every visit is built around
              the children taking part. If you'd like an author to visit your setting,{" "}
              <a href="mailto:hello@learnwithbailey.org" className="font-semibold text-terracotta underline decoration-terracotta/40 hover:decoration-terracotta">
                get in touch <Mail size={14} className="inline-block align-[-2px]" />
              </a>
              .
            </p>
          </Reveal>

          <Reveal>
            <div className="mt-16 pt-10 border-t-2 border-dashed border-sand text-center">
              <img
                src="/images/damali-author.webp"
                alt="Damali Allison, children's author"
                className="w-24 h-24 rounded-full object-cover mx-auto mb-5 border-4 border-white shadow-lg"
              />
              <p className="font-display font-extrabold text-2xl sm:text-3xl text-walnut">I am so glad you're here!</p>
              <p className="mt-2 font-display font-bold text-lg text-terracotta">Damali Allison</p>
              <p className="text-sm text-walnutmuted">Your author and friend</p>
            </div>
          </Reveal>

        </article>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
